// Al tekst i spillet: kort, varm og på dansk. Ingen salgssprog, ingen tankestreger.

import { roleOf } from '../sim/roles';
import type { ActionKind, ApplyError, ThreatKind } from '../sim';

/** Solo står uret stille, mens et hændelseskort venter på svar (Spor: kortet). */
export const CARD_FIRST = 'Svar på kortet først. Så går tiden igen.';
/** Kortets fodnote, mens uret står stille bag det (solo). */
export const CARD_PAUSED = 'Tiden står stille, til du har valgt.';
/** Fodnoten, når makkeren (Volt) svarer på kortet, mens uret står stille. */
export const CARD_PAUSED_PARTNER = (who: string): string => `${who} vælger. Tiden står stille imens.`;
/** Fodnoten, når makkeren svarer, og uret går: standardsvaret er stadig hans, ikke spillerens. */
export const CARD_PARTNER = (who: string, deflt: string): string => `Vælger ${who} ikke: ${deflt}.`;
/** Fanen, kortet står som bag en menu: et tryk henter det frem. */
export const CARD_PEEK = 'Svar på kortet';
/** Samme, kort nok til en knap i ringmenuen og boblen ved fingeren. */
export const CARD_FIRST_SHORT = 'Svar først';

export function errorText(kind: ActionKind | null, error: ApplyError | 'paused', partner: string | null = null): string {
  if (kind === 'reinforce_mast' && error === 'not_needed') return 'Forstærk masterne, når en storm er på vej.';
  if (kind === 'call_year' && error === 'busy') return 'Næste år, når nettet er roligt: alle byer tændt, ingen brud og intet uvejr.';
  if (kind === 'call_year' && error === 'not_needed') return 'Året er ikke slut endnu. Der kommer mere.';
  if (kind === 'upgrade_line' && error === 'not_allowed') return 'Ledningen er allerede så stærk, som den kan blive. Byg en ledning mere ved siden af.';
  if (kind === 'upgrade_line' && error === 'busy') return 'Ledningen skal være hel, før den kan opgraderes.';
  if (kind === 'build_line' && error === 'bad_option') return 'Havvind skal i land ved kysten. Træk til en kystby eller en mølle ved havet.';
  if (kind === 'demolish' && error === 'not_allowed') return 'Den ledning hører til banen og bliver stående.';
  if (kind === 'upgrade_station' && error === 'already_exists') return 'Stationen er allerede opgraderet.';
  if (kind === 'dispatch_crew' && error === 'not_needed') return 'Ledningen er hel. Montøren bliver hjemme.';
  switch (error) {
    case 'paused':
      return 'Spillet holder pause. Tryk på afspil for at bygge.';
    case 'wrong_phase':
      return 'Ikke lige nu.';
    case 'unknown_target':
      return 'Der kan ledningen ikke gå hen.';
    case 'not_unlocked':
      return 'Det låser du op på en senere bane.';
    case 'too_poor':
      return 'Der er ikke penge nok til det. Der drypper flere ind hele året.';
    case 'no_slots':
      return 'Der er ikke flere pladser til ledninger. En station giver flere.';
    case 'already_exists':
      return 'Den er der allerede.';
    case 'too_far':
      return 'For langt. Byg via en by eller station på vejen.';
    case 'too_late':
      return 'Stormen er her. Forstærk, mens den er varslet.';
    case 'wrong_area':
      return 'Øst og vest er to net. Det kræver et særligt kabel.';
    case 'bad_option':
      return 'Den slags ledning kan ikke gå der.';
    case 'not_needed':
      return 'Det er der ikke brug for lige nu.';
    case 'no_crew':
      return 'Alle montører har travlt. Hyr et hold mere.';
    case 'busy':
      return 'Der bliver allerede arbejdet på den.';
    case 'duplicate':
      return 'Det er allerede sat i gang.';
    case 'not_allowed':
      return 'Det kan man ikke her.';
    case 'other_role':
      // IGN-løkke 2, runde 3: "Ping din makker" i solo, hvor Volt er makkeren. Han kan spørges i ringen.
      if (partner) return `Det er ${partner}s job. Tryk på stedet og spørg ${partner}.`;
      return kind && roleOf(kind) === 'p1'
        ? 'Det er Planlæggerens job. Ping din makker, så bygger I den.'
        : 'Det er Driftsvagtens job. Ping din makker, så klarer I den.';
  }
}

export const THREAT_NAMES: Partial<Record<ThreatKind, string>> = {
  storm: 'Storm',
  digger: 'Gravko',
  anchor: 'Ankerbåd',
  protest: 'Naboprotest',
  spike: 'Spidsbølge',
  heatwave: 'Hedebølge',
  dc_rush: 'Datacenter-kø',
  duo: 'Fælles lyn',
  dunkelflaute: 'Vindstille',
  cable_half: 'Kabelskade',
  plant_trip: 'Værksnedbrud',
};

/**
 * Et tryk på en trussel, der ikke kan jages, siger hvad der virker i stedet. IGN-løkke 2, runde 2:
 * "ingen lyd, ingen ryst og ingen tekst; det ligner, at klikket ikke blev registreret".
 */
export function chaseText(kind: ThreatKind, norway: boolean): string {
  const name = THREAT_NAMES[kind] ?? 'Den';
  switch (kind) {
    case 'storm':
      return 'En storm kan ikke jages. Forstærk masterne i dens bane.';
    case 'dunkelflaute':
      return norway ? 'Vindstille kan ikke jages. Ring til Norge!' : 'Vindstille kan ikke jages. Lad batterierne tage over.';
    case 'spike':
    case 'heatwave':
      return `${name} kan ikke jages. Giv byerne mere strøm, eller tryk Lynreserve.`;
    case 'plant_trip':
      return 'Et værksnedbrud kan ikke jages. Tryk Lynreserve, eller byg et svinghjul.';
    case 'cable_half':
      return 'Kabelskaden kan ikke jages. Byg en omvej.';
    default:
      return `${name} kan ikke jages. Følg hintet nederst.`;
  }
}

const NUM = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 });
const DEC = new Intl.NumberFormat('da-DK', { maximumFractionDigits: 1 });
const PLURAL = new Intl.PluralRules('da-DK');

/** Et heltal med dansk tusindtalsskiller: 128.451. */
export function num(n: number): string {
  return NUM.format(Math.trunc(n));
}

/** Et beløb i kroner: "1 kr", "128.451 kr". */
export function kr(n: number): string {
  return `${num(n)} kr`;
}

/** Et beløb kort nok til HUD'ens smalle celle. Fuldt under 100.000 kr (alle realistiske kasser),
 *  derover "128 t. kr" og "9,8 mio. kr", altid rundet ned, så kassen aldrig lover for meget.
 *  Det fulde beløb står i title og aria-label. */
export function krShort(n: number): string {
  const a = Math.abs(n);
  if (a < 100_000) return kr(n);
  if (a < 1_000_000) return `${num(n / 1000)} t. kr`;
  return `${DEC.format(Math.trunc(n / 100_000) / 10)} mio. kr`;
}

/** Tal og navneord i ental eller flertal efter dansk grammatik: plural(1, 'by', 'byer') giver "1 by". */
export function plural(n: number, one: string, other: string): string {
  return `${num(n)} ${PLURAL.select(n) === 'one' ? one : other}`;
}
